import { get, compact, noop, flatten, overSome, negate } from 'lodash/fp'
import {
  createField,
  createSection,
  createSectionCreator,
  getBankAccountFormat,
  getPromptpayAccountFormat,
} from 'e-submission/domain/data-model/form/utils'

import VALUES from 'core/data-model/constants/values'

import {
  getSelectedPersonalTitleList,
  getDCABankList,
  isSectionValid,
  isCheckAccountDCA,
  isSelectedDcaBankAccount,
} from 'e-submission/apps/selectors'

import { isDCASigned, isDCAInformation, isForeigner } from 'e-submission/domain/data-model/form/selectors'
import {
  setAppValue,
  updateDCA,
  signedDCA,
  VerifyCheckAccountDCA,
  checkAccount,
  confirmSubmitDCA,
} from 'e-submission/apps/actions'
import { signatureField, documentField } from 'e-submission/domain/data-model/submit'

import MESSAGES from 'core/data-model/constants/messages'
import { getToggles } from 'quick-quote/feature-toggles'
import store from 'e-submission/store'

const PREFIX_FORM = 'DCA'

export const DCAFormSection = (app) => {
  const isSelectedBankAccount = isSelectedDcaBankAccount(app) && !getToggles().ENABLE_DCA_NONE_SELECT_BANK
  const dcaBankAccountOrPromptpay = isSelectedBankAccount
    ? `${PREFIX_FORM}.accountNumber`
    : `${PREFIX_FORM}.idNo.citizenId`

  const isPayer = app?.insured?.isPayer
  const titleValue = !isPayer ? 'payer' : 'insured'

  const isForeignerInsured = app.insured.nationality.value !== 'Thai'
  const isForeignerPayer = app.payer && app.payer.nationality.value !== 'Thai'

  const isForeignerInsuredAlsoPayer = isForeignerInsured && isPayer

  return compact([
    createField(`${PREFIX_FORM}.type`, [
      {
        label: 'กรุณาเลือกช่องทางการขอรับผลประโยชน์ตามกรมธรรม์',
        id: `${PREFIX_FORM}.dcaType`,
        c: 'RadioGroup',
        required: true,
        p: {
          disabled: isForeignerInsuredAlsoPayer || isForeignerPayer ? true : false, //!TODO: Update based on signed or not signed on DCA
          mergeState: (state, app, { dispatch = noop } = {}) => {
            const isInsuredHasPayer = app.payer //! if value is undefined which mean there is no payer

            return {
              customOnClick: (option) => {
                dispatch(setAppValue('DCA.dcaType', option))

                if (option.value === 'BANK_ACCOUNT') {
                  if (app.DCA.isAccountChecked) {
                    dispatch(setAppValue('DCA.accountName', app.DCA.isAccountChecked.bankBookName))
                  } else {
                    //! set empty string for now
                    dispatch(setAppValue('DCA.accountName', ''))
                  }
                } else {
                  if (isInsuredHasPayer) {
                    dispatch(
                      setAppValue(
                        'DCA.accountName',
                        `${app.payer.title.text} ${app.payer.firstName} ${app.payer.lastName}`
                      )
                    )
                    dispatch(setAppValue('DCA.idNo.citizenId', `${app.payer?.idNo?.citizenId}`))
                  } else {
                    dispatch(
                      setAppValue(
                        'DCA.accountName',
                        `${app.insured.title.text} ${app.insured.firstName} ${app.insured.lastName}`
                      )
                    )
                    dispatch(setAppValue('DCA.idNo.citizenId', `${app.insured?.idNo?.citizenId}`))
                  }
                }
              },
              options: [
                { text: 'บัญชีธนาคาร', value: 'BANK_ACCOUNT' },
                { text: 'พร้อมเพย์ (ID Card)', value: 'PROMPTPAY' },
              ],
            }
          },
        },
      },
    ]),
    createField(`${PREFIX_FORM}.title`, [
      {
        label: 'คำนำหน้า',
        id: `${PREFIX_FORM}.title`,
        c: 'Dropdown',
        disabled: true,
        required: true,
        p: {
          mergeState: (state) => ({
            options: getSelectedPersonalTitleList(state, titleValue),
          }),
        },
      },
    ]),
    createField(`${PREFIX_FORM}.firstName`, [
      {
        label: 'ชื่อเจ้าของบัญชี',
        id: `${PREFIX_FORM}.firstName`,
        c: 'TextInput',
        disabled: true,
        required: true,
        p: {},
      },
    ]),
    createField(`${PREFIX_FORM}.lastName`, [
      {
        label: 'นามสกุลเจ้าของบัญชี',
        id: `${PREFIX_FORM}.lastName`,
        c: 'TextInput',
        disabled: true,
        required: true,
        p: {},
      },
    ]),
    isSelectedBankAccount &&
      createField(`${PREFIX_FORM}.bank`, [
        {
          label: 'ยินยอมขอรับผลประโยชน์ผ่านบัญชีธนาคาร',
          id: `${PREFIX_FORM}.bank`,
          c: 'Dropdown',
          required: true,
          p: {
            placeHolder: 'กรุณาเลือกธนาคาร',
            mergeState: (state, app) => {
              return {
                options: getDCABankList(state),
                disabled: false,
              }
            },
          },
        },
      ]),
    createField(dcaBankAccountOrPromptpay, [
      {
        label: isSelectedBankAccount ? `${MESSAGES.ACCOUNT_NUMBER}` : 'เลขบัญชีพร้อมเพย์',
        id: dcaBankAccountOrPromptpay,
        c: 'NumberInput',
        required: true,
        p: {
          mergeState: (state, app) => {
            const bankCode = get(`${PREFIX_FORM}.bank.value`, app)
            const { format } = isSelectedBankAccount ? getBankAccountFormat(bankCode) : getPromptpayAccountFormat()

            return { format, disabled: !isSelectedBankAccount ? true : false }
          },
        },
      },
    ]),
    getToggles().ENABLE_OCR ||
      (getToggles().ENABLE_CHECKACC &&
        createField(`${PREFIX_FORM}.accountName`, [
          {
            label: `${MESSAGES.ACCOUNT_NAME}`,
            id: `${PREFIX_FORM}.accountName`,
            c: 'TextInput',
            required: false,
            p: {
              mergeState: (state, app) => {
                const isEnableAccountName = get(`${PREFIX_FORM}.isEnableAccountName`, app)
                return {
                  disabled: isEnableAccountName ? false : true,
                }
              },
            },
          },
        ])),
  ])
}

export const DCASignatureSection = (app) => {
  const isInsuredHasPayer = !app?.insured?.isPayer

  const createSignatureField = isInsuredHasPayer
    ? signatureField('payer', 'DCA')(app, 'payer.DCA', {
        label: 'ลงนาม ผู้ชำระเบี้ย',
      })
    : signatureField('insured')(app, 'insured.DCA', {
        label: 'ลงนาม ผู้เอาประกันภัย',
      })

  return compact([
    createField(
      'DCA.edit',
      [
        {
          id: 'DCA.edit',
          c: 'EditCard',
          p: {
            title: 'ข้อมูลที่กรอกไปถูกนำมาสร้างหนังสือยินยอม',
            children: 'การแก้ไขข้อมูลจะต้องลงนามใหม่',
            mergeState: (state, app) => ({
              disabled: isDCASigned(app),
            }),
            mergeProps: ({ dispatch }) => ({
              onClick: () => {
                store.dispatch(setAppValue('DCA.status', VALUES.DCA_STATUS.INFORMATION))
              },
            }),
          },
        },
      ],
      {
        pl: 0,
        pr: 0,
      }
    ),
    createField('insured.DCALabel', [
      {
        id: 'insured.DCALabel',
        c: 'SubHeading',
        p: {
          children: 'ลงนาม ผู้เอาประกันภัย',
        },
      },
    ]),
    createSignatureField,
  ])
}

export const DCASection = createSectionCreator((app, id, section) => {
  const isSelectedBankAccount = isSelectedDcaBankAccount(app) && !getToggles().ENABLE_DCA_NONE_SELECT_BANK
  const isDCABank = isSelectedDcaBankAccount(app)
  let isShowCheckAccount = false
  if (getToggles().ENABLE_CHECKACC) {
    isShowCheckAccount = isCheckAccountDCA(app)
  }

  const DCAForm = [
    ...DCAFormSection(app),
    isShowCheckAccount && isSelectedBankAccount
      ? createField('DCA.BTNcheckaccount', [
          {
            id: 'DCA.VerifyCheckAccount',
            c: 'Button',
            p: {
              block: true,
              children: 'ตรวจสอบ',
              mergeState: (state, app, { dispatch = noop, history, ...props } = {}) => {
                return {
                  disabled: overSome([
                    negate(
                      isSectionValid(state, [createSection(documentField(id)(app, 'insured.DCA_BANK_BOOK', section))])
                    ),
                    negate(isSectionValid(state, [createSection(DCAFormSection(app))])),
                  ])(app),
                  onClick: () => {
                    dispatch(checkAccount('DCA', null))
                  },
                }
              },
            },
          },
        ])
      : app?.DCA?.dcaType?.value === 'PROMPTPAY'
      ? createField('DCA.sign', [
          {
            id: 'DCA.signPP',
            c: 'Button',
            p: {
              block: true,
              children: 'ลงนาม',
              mergeState: (state, app, { dispatch = noop, history, ...props } = {}) => ({
                //! if bookbank is upload and all sections are valid ---> ENABLED
                disabled:
                  overSome([
                    negate(
                      isSectionValid(state, [createSection(documentField(id)(app, 'insured.DCA_BANK_BOOK', section))])
                    ),
                    negate(isSectionValid(state, [createSection(DCAFormSection(app))])),
                  ])(app) &&
                  //! TODO: Might need a better condional here
                  //! If selected PROMPTPAY and all input sections are valid BOOK_BANK is NOT REQUIRED
                  !(
                    app?.DCA?.dcaType?.value === 'PROMPTPAY' &&
                    isSectionValid(state, [createSection(DCAFormSection(app))])(app)
                  ),
                onClick: () => {
                  dispatch(confirmSubmitDCA())
                },
              }),
            },
          },
        ])
      : createField('DCA.sign', [
          {
            id: 'DCA.sign',
            c: 'Button',
            p: {
              block: true,
              children: 'ลงนาม',
              mergeState: (state, app, { dispatch = noop, history, ...props } = {}) => ({
                //! if bookbank is upload and all sections are valid ---> ENABLED
                disabled:
                  overSome([
                    negate(
                      isSectionValid(state, [createSection(documentField(id)(app, 'insured.DCA_BANK_BOOK', section))])
                    ),
                    negate(isSectionValid(state, [createSection(DCAFormSection(app))])),
                  ])(app) &&
                  //! TODO: Might need a better condional here
                  //! If selected PROMPTPAY and all input sections are valid BOOK_BANK is NOT REQUIRED
                  !(
                    app?.DCA?.dcaType?.value === 'PROMPTPAY' &&
                    isSectionValid(state, [createSection(DCAFormSection(app))])(app)
                  ),
                onClick: () => {
                  dispatch(updateDCA())
                },
              }),
            },
          },
        ]),
  ]

  const DCASignature = [
    ...DCASignatureSection(app),
    createField('DCA.submit', [
      {
        id: 'DCA.submit',
        c: 'Button',
        p: {
          block: true,
          children: 'ตกลง',
          mergeState: (state, app, extraObject) => {
            const bookBankCondition = negate(
              isSectionValid(state, [createSection(documentField(id)(app, 'insured.DCA_BANK_BOOK', section))])
            )
            const dcaFormSectionsValidCondition = negate(
              isSectionValid(state, [createSection(DCASignatureSection(app))])
            )
            const signDcaButtonConditions = [dcaFormSectionsValidCondition, isDCASigned]

            //! If selected promptpay, then we enable sign button
            if (app?.DCA?.dcaType?.value !== 'PROMPTPAY') {
              signDcaButtonConditions.unshift(bookBankCondition)
            }
            return {
              disabled: overSome(signDcaButtonConditions)(app),
              onClick: () => {
                extraObject.dispatch(signedDCA())
                extraObject.EtoggleModal()
              },
            }
          },
        },
      },
    ]),
  ]

  const isInformation = isDCAInformation(app)

  //! If selected promptpay, then we hide the book bank section
  const dcaBookbankSection = isSelectedBankAccount ? documentField(id)(app, 'insured.DCA_BANK_BOOK', section) : []

  return flatten([dcaBookbankSection, isInformation ? DCAForm : DCASignature])
})
